import { FC } from "react";

interface Props {
	/** Whether the picker is currently fetching results. */
	isSearching?: boolean;
	/** Number of results in the current visible result set. */
	count: number;
	/** Whether the dropdown is open and showing results. When false the
	 *  announcer is silent — we don't want to announce results that
	 *  aren't visible (selection locked, query below threshold, etc.) */
	isOpen: boolean;
	/** Singular noun for one result, e.g. "store". */
	subjectSingular: string;
	/** Plural noun for multiple results, e.g. "stores". */
	subjectPlural: string;
	/** The current debounced query — drives the empty-state message. */
	query: string;
}

/**
 * Polite live region for typeahead pickers. Announces "12 stores
 * found" / "No stores match query" / "Searching…" so screen-reader
 * users get the same feedback sighted users get from the dropdown
 * appearing.
 *
 * Visually hidden via `sr-only` — purely an AT surface.
 *
 * Pair with `useComboPicker`. Keep this component focused on the
 * announcement only; the picker still renders its own dropdown.
 */
const ResultCountAnnouncer: FC<Props> = ({
	isSearching,
	count,
	isOpen,
	subjectSingular,
	subjectPlural,
	query
}) => {
	if (!isOpen) return null;
	if (!query) return null;

	let message: string;
	if (isSearching) {
		message = `Searching for ${query}`;
	} else if (count === 0) {
		message = `No ${subjectPlural} match ${query}`;
	} else if (count === 1) {
		message = `1 ${subjectSingular} found`;
	} else {
		message = `${count} ${subjectPlural} found`;
	}

	return (
		<div
			role="status"
			aria-live="polite"
			aria-atomic="true"
			className="sr-only"
		>
			{message}
		</div>
	);
};

export default ResultCountAnnouncer;
